import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useGlobalContext } from '../../../../../../../context/GlobalState'
import { FacturaAPI } from '../../../../../../../api/facturas.api'
import { buildFullDocument } from '../../utils/utils'
import { getErrorMessage } from '../../utils/utils'
import { sileo } from 'sileo'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'

import FacturaTotales from './FacturaTotales'
import { emptyIssuer, emptyReceiver, emptyDetail, emptyHeader, emptyReference } from '../../data'

export default function FacturaFormSimplified({
  onContinue,
  selectedDocType,
  onBack,
  onReset,
  initialData,
  initialClone
}) {
  const navigate = useNavigate()
  const { currentBusiness, masterEntityData } = useGlobalContext()
  const facturasAPI = new FacturaAPI()

  // Estados del documento
  const [dteCode, setDteCode] = useState(selectedDocType?.id || '33')
  const [dateIssued, setDateIssued] = useState('')
  const [folio, setFolio] = useState('')
  const [issuer, setIssuer] = useState({ ...emptyIssuer })
  const [receiver, setReceiver] = useState({ ...emptyReceiver })
  const [header, setHeader] = useState({ ...emptyHeader })
  const [details, setDetails] = useState([{ ...emptyDetail }])
  const [references, setReferences] = useState([])

  // nuevo estado para mostrar/ocultar referencias
  const [showReferencesBox, setShowReferencesBox] = useState(false)

  // Flags/UI
  const xUseDefaults = true
  const [isEmitting, setIsEmitting] = useState(false)

  // Secciones plegables
  const [showIssuer, setShowIssuer] = useState(true)
  const [showReceiver, setShowReceiver] = useState(true)

  // UI Cod Producto
  const [useProductCode, setUseProductCode] = useState(false)

  // --- NUEVO: RUT receptor separado cuerpo + DV ---
  const [receiverRutBody, setReceiverRutBody] = useState('')
  const [receiverRutDv, setReceiverRutDv] = useState('')
  const [isSearchingEntity, setIsSearchingEntity] = useState(false)

  const splitRut = rutString => {
    if (!rutString) return { body: '', dv: '' }
    const clean = rutString.replace(/\./g, '').replace(/-/g, '').trim()
    if (!clean) return { body: '', dv: '' }
    const dv = clean.slice(-1)
    const body = clean.slice(0, -1)
    return { body, dv }
  }

  // Si cambia el tipo de documento elegido en el paso anterior, sincronizamos dteCode
  useEffect(() => {
    if (selectedDocType?.id) {
      setDteCode(selectedDocType.id)
    }
  }, [selectedDocType])

  // Handlers simples
  const handleIssuer = e => setIssuer({ ...issuer, [e.target.name]: e.target.value })
  const handleReceiver = e => setReceiver({ ...receiver, [e.target.name]: e.target.value })
  const handleHeader = e => setHeader({ ...header, [e.target.name]: e.target.value })

  // Detalles: agregar, eliminar, actualizar
  const addDetail = () =>
    setDetails(arr => [...arr, { ...emptyDetail, line_number: arr.length + 1 }])

  const removeDetail = idx =>
    setDetails(arr => arr.filter((_, i) => i !== idx).map((d, i) => ({ ...d, line_number: i + 1 })))

  const updateDetail = (idx, field, value) =>
    setDetails(arr => arr.map((d, i) => (i === idx ? { ...d, [field]: value } : d)))

  // Referencias: agregar, eliminar, actualizar
  const addReference = () => setReferences(arr => [...arr, { ...emptyReference }])
  const removeReference = idx => setReferences(arr => arr.filter((_, i) => i !== idx))
  const updateReference = (idx, field, value) =>
    setReferences(arr => arr.map((r, i) => (i === idx ? { ...r, [field]: value } : r)))

  // Emisor auto-cargado desde el contexto global
  useEffect(() => {
    if (!masterEntityData || initialData) return

    const entity = masterEntityData
    const mainAddress = entity.addresses?.[0]
    const mainActivity = entity.activities?.[0]

    setIssuer({
      rut: entity.tax_id.replace(/\./g, ''),
      business_name: entity.name,
      phone_number: mainAddress?.phone || '',
      email: currentBusiness?.email || '',
      business_activity: mainActivity?.name || '',
      activity_code: mainActivity?.code || '',
      sii_branch_code: mainAddress?.sii_branch_code || '',
      address: mainAddress?.address || '',
      district: mainAddress?.district?.name || '',
      city: mainAddress?.district?.city?.name || ''
    })

    const today = new Date().toISOString().split('T')[0]
    if (!initialData) setDateIssued(today)
  }, [masterEntityData, initialData, currentBusiness?.email])

  // --- NUEVA LÓGICA DE CLONADO ---
  useEffect(() => {
    if (initialClone && !initialData) {
      // Usar fecha de hoy para el clon
      setDateIssued(new Date().toISOString().split('T')[0])

      // Parsear receptor
      const rawRut = initialClone.document_receiver?.rut || initialClone.receiver_tax_id || ''
      const cleanRut = rawRut.replace(/\./g, '').trim()
      const { body, dv } = splitRut(cleanRut)
      setReceiverRutBody(body)
      setReceiverRutDv(dv.toUpperCase())

      setReceiver(prev => ({
        ...prev,
        rut: cleanRut,
        business_name: initialClone.document_receiver?.business_name || initialClone.receiver_name || '',
        business_activity: initialClone.document_receiver?.business_activity || '',
        contact: initialClone.document_receiver?.contact || '',
        address: initialClone.document_receiver?.address || '',
        district: initialClone.document_receiver?.district || '',
        city: initialClone.document_receiver?.city || ''
      }))

      // Parsear detalles
      if (initialClone.details && Array.isArray(initialClone.details) && initialClone.details.length > 0) {
        const mappedDetails = initialClone.details.map((d, index) => {
          const description = d.item_description || d.description || ''
          return {
            ...emptyDetail,
            line_number: d.line_number || index + 1,
            item_name: d.item_name || d.name || d.detail_name || '',
            item_description: description,
            showDescription: !!description,
            quantity: d.quantity ? Math.round(Number(d.quantity)) : 1,
            unit_price: d.unit_price ? Math.round(Number(d.unit_price)) : 0,
            discount_percent: d.discount_percent ? Math.round(Number(d.discount_percent)) : 0,
            unit: d.unit || 'UN',
            item_code: typeof d.item_code === 'string' ? d.item_code.slice(0, 100) : (typeof d.product_code === 'string' ? d.product_code.slice(0, 100) : ''),
            item_type_code: d.item_type_code || ''
          }
        })
        setDetails(mappedDetails)

        // Si algún detalle tiene código, activamos el flag
        if (mappedDetails.some(m => m.item_code)) {
          setUseProductCode(true)
        }
      }
    }
  }, [initialClone, initialData])

  // --- RUT receptor: cambios en inputs ---
  const handleReceiverRutBodyChange = e => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 8) // solo números
    setReceiverRutBody(value)
    setReceiver(prev => ({
      ...prev,
      rut: (value + (receiverRutDv || '')).toUpperCase()
    }))
  }

  const handleReceiverRutDvChange = e => {
    const value = e.target.value
      .slice(0, 1)
      .replace(/[^0-9Kk]/g, '')
      .toUpperCase()
    setReceiverRutDv(value)

    const fullRut = (receiverRutBody || '') + value
    setReceiver(prev => ({
      ...prev,
      rut: fullRut
    }))

    // Gatillar búsqueda si el RUT parece completo (8 dígitos + DV)
    if (value && receiverRutBody.length === 8) {
      handleSearchReceiver(fullRut)
    }
  }

  const handleSearchReceiver = async rutToSearch => {
    const rut = rutToSearch?.trim()
    if (!rut || rut.length < 8) return

    try {
      setIsSearchingEntity(true)
      const res = await sileo.promise(facturasAPI.getSearchEntityRut(rut), {
        loading: { title: 'Buscando entidad...' },
        success: { title: 'Entidad encontrada', description: 'Datos del receptor cargados exitosamente.' },
        error: { title: 'Entidad no encontrada', description: 'Revisa tu RUT y verifica que esté correcto.' }
      })

      if (res?.data) {
        const entity = res.data
        const mainAddress = entity.addresses?.[0]
        const mainActivity = entity.activities?.[0]

        const cleanRut = entity.tax_id.replace(/\./g, '')
        const { body, dv } = splitRut(cleanRut)

        setReceiver(prev => ({
          ...prev,
          rut: cleanRut,
          business_name: entity.name,
          contact: mainAddress?.phone || '',
          business_activity: mainActivity?.name || '',
          address: mainAddress?.address || '',
          district: mainAddress?.district?.name || '',
          city: mainAddress?.district?.city?.name || ''
        }))

        setReceiverRutBody(body)
        setReceiverRutDv(dv.toUpperCase())
      }
    } catch (err) {
      // Manejado por sileo.promise
    } finally {
      setIsSearchingEntity(false)
    }
  }

  // Receptor por RUT: se mantiene blur como respaldo
  const handleReceiverRutBlur = () => {
    const rut = receiver.rut?.trim()
    if (rut && rut.length === 9) {
      handleSearchReceiver(rut)
    }
  }

  const buildDocs = () => {
    const fullDoc = buildFullDocument({
      dateIssued,
      folio,
      dteCode,
      issuer,
      receiver,
      details,
      header,
      references,
      jsonParam: ''
    })

    return { fullDoc }
  }

  const handleReset = () => {
    const today = new Date().toISOString().split('T')[0]

    // Tipo de documento
    setDteCode(selectedDocType?.id || '33')

    // Fecha emisión
    setDateIssued(today)

    // Folio
    setFolio('')

    // Receptor
    setReceiver({ ...emptyReceiver })
    setReceiverRutBody('')
    setReceiverRutDv('')

    // Encabezado
    setHeader({ ...emptyHeader })

    // Detalle: una sola línea vacía
    setDetails([{ ...emptyDetail }])

    // Referencias
    setReferences([])
    setShowReferencesBox(false)

    // UI
    setUseProductCode(false)

    if (onReset) onReset()
  }

  // Enviar documento (solo la emisión -- si esto tira error, la factura no se creó)
  const emitFactura = async () => {
    const { fullDoc } = buildDocs()
    const documents = [fullDoc]
    const payload = { documents }

    const res = await facturasAPI.emitirFactura(currentBusiness.id, xUseDefaults, payload)
    const documentId = res.data?.response_tupana?.documents?.[0]?.document?.id
    if (!documentId) throw new Error('No se pudo obtener el ID del documento en Tupana')
    return documentId
  }

  // Pollea el estado ya emitido. Un error acá (red, CORS, etc.) NO significa que la
  // emisión haya fallado -- la emisión ya terminó antes de llegar a esta función.
  const pollFacturaEstado = async documentId => {
    const POLL_SUCCESS_STATUSES = ['issued', 'delivered', 'paid', 'unpaid']
    const POLL_ERROR_STATUSES = ['rejected', 'cancelled', 'issue_failed', 'invalid', 'permanent_error']
    const POLL_INTERVAL_MS = 4000
    const POLL_MAX_ATTEMPTS = 30 // ~2 minutos

    for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
      await new Promise(res => setTimeout(res, POLL_INTERVAL_MS))
      try {
        const statusRes = await facturasAPI.getEstadoLocal(documentId)
        const status = statusRes.data?.status

        if (POLL_SUCCESS_STATUSES.includes(status)) return { type: 'issued' }
        if (POLL_ERROR_STATUSES.includes(status)) return { type: 'rejected' }
      } catch (err) {
        console.error('Error consultando estado-local (la factura ya fue emitida):', err)
        // seguimos intentando; no cortamos el polling por un error puntual de red/CORS
      }
    }
    return { type: 'unknown' }
  }

  const handleSubmit = async e => {
    e.preventDefault()

    if (onContinue) {
      const { fullDoc } = buildDocs()

      const formState = {
        dateIssued,
        folio,
        issuer,
        receiver,
        header,
        details,
        references,
        dteCode,
        useProductCode,
        showReferencesBox
      }

      onContinue({ fullDoc, dteCode, formState })
      return
    }

    try {
      setIsEmitting(true)

      let documentId
      try {
        documentId = await sileo.promise(emitFactura(), {
          loading: { title: 'Enviando la factura al SII...' },
          success: { title: 'Aviso', description: 'Documento enviado, validando con el SII...' },
          error: {
            title: 'Error de Emisión',
            description: err => getErrorMessage(err, 'No se pudo crear tu factura 🤯')
          }
        })
      } catch (emitErr) {
        // Error ya mostrado por el toast de sileo.promise; acá la emisión sí falló.
        return
      }

      // A partir de acá la emisión ya está confirmada; lo que sigue es solo
      // seguimiento de estado, nunca debe reportarse como "falló la emisión".
      const result = await pollFacturaEstado(documentId)

      if (result.type === 'issued') {
        sileo.success({
          title: '¡Éxito!',
          description: (
            <span className='text-green-500/80 font-medium'>Factura creada exitosamente 👌</span>
          )
        })
        navigate('/dashboard/facturacion')
      } else if (result.type === 'rejected') {
        sileo.error({
          title: 'Documento Rechazado',
          description: 'Factura rechazada por el SII.'
        })
        navigate('/dashboard/facturacion')
      } else {
        sileo.warning({
          title: 'Enviada, confirmación pendiente',
          description:
            'La factura se envió correctamente al SII. Todavía no pudimos confirmar el estado final -- revisa el listado en unos minutos.'
        })
        navigate('/dashboard/facturacion')
      }
    } catch (err) {
      // Error inesperado fuera del flujo de emisión/polling.
    } finally {
      setIsEmitting(false)
    }
  }

  const SectionHeader = ({ title, open, onToggle }) => (
    <button
      type='button'
      onClick={onToggle}
      className='flex w-full items-center justify-between text-left px-4 py-2 bg-gray-100 border-b border-gray-200'
    >
      <span className='font-semibold text-sm md:text-base'>{title}</span>
      <KeyboardArrowDownIcon
        fontSize='small'
        className={`transition-transform duration-200 transform ${open ? 'rotate-0' : 'rotate-180'
          }`}
      />
    </button>
  )

  useEffect(() => {
    if (!initialData) return

    const {
      dateIssued: initDateIssued,
      folio: initFolio,
      issuer: initIssuer,
      receiver: initReceiver,
      header: initHeader,
      details: initDetails,
      references: initReferences,
      dteCode: initDteCode,
      useProductCode: initUseProductCode,
      showReferencesBox: initShowReferencesBox
    } = initialData

    setDteCode(initDteCode || selectedDocType?.id || '33')
    setDateIssued(initDateIssued || '')
    setFolio(initFolio || '')
    setIssuer(initIssuer || { ...emptyIssuer })
    setReceiver(initReceiver || { ...emptyReceiver })
    setHeader(initHeader || { ...emptyHeader })
    setDetails(initDetails && initDetails.length > 0 ? initDetails : [{ ...emptyDetail }])
    setReferences(initReferences || [])
    setUseProductCode(!!initUseProductCode)
    setShowReferencesBox(!!initShowReferencesBox && (initReferences?.length ?? 0) > 0)

    // RUT separado
    if (initReceiver?.rut) {
      const { body, dv } = splitRut(initReceiver.rut)
      setReceiverRutBody(body)
      setReceiverRutDv((dv || '').toUpperCase())
    } else {
      setReceiverRutBody('')
      setReceiverRutDv('')
    }
  }, [initialData, selectedDocType])
  // Texto del tipo de documento para el rectángulo gris
  const docTitle =
    selectedDocType?.label?.toUpperCase() ||
    (dteCode === '34' ? 'FACTURA NO AFECTA O EXENTA ELECTRÓNICA' : 'FACTURA ELECTRÓNICA')

  return (
    <div className='min-h-screen w-full flex justify-center bg-gray-50'>
      <form
        onSubmit={handleSubmit}
        className='w-full max-w-5xl space-y-6 border-gray-300 rounded-md p-2 border-2'
      >
        {/* === RECTÁNGULO GRIS + FECHA EMISIÓN === */}
        <section className='flex justify-end'>
          <div className='w-full md:w-1/2'>
            <div className='bg-gray-100 border border-gray-300 rounded-md px-4 py-3 text-center text-sm'>
              <div className='font-semibold'>Rut {issuer.rut || currentBusiness?.rut || ''}</div>
              <div className='font-semibold'>{docTitle}</div>
              <div className='font-semibold'>N° folio no asignado</div>
            </div>
            <div className='mt-3 flex items-stretch bg-gray-100 border border-gray-300 rounded-md overflow-hidden'>
              <label className='px-3 text-xs flex items-center bg-gray-100 w-28'>
                Fecha Emisión
              </label>
              <div className='flex-1 border-l border-gray-300'>
                <input
                  type='date'
                  className='w-full p-2 text-sm border-none focus:outline-none'
                  value={dateIssued}
                  onChange={e => setDateIssued(e.target.value)}
                  required={!xUseDefaults}
                />
              </div>
            </div>
          </div>
        </section>

        {/* === DATOS EMISOR === */}
        <section className='bg-white rounded-md shadow border border-gray-200'>
          <SectionHeader
            title='Datos Emisor'
            open={showIssuer}
            onToggle={() => setShowIssuer(v => !v)}
          />

          {showIssuer && (
            <div className='px-4 pb-4 pt-3 space-y-3'>
              {/* Razón social (fila completa) */}
              <div className='grid grid-cols-1'>
                <div className='flex'>
                  <span className='issuer-label-pill'>Razón Social</span>
                  <input
                    name='business_name'
                    className='input issuer-input'
                    value={issuer.business_name || ''}
                    onChange={handleIssuer}
                  />
                </div>
              </div>

              {/* 2 columnas */}
              <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                {/* Izquierda */}
                <div className='space-y-3'>
                  <div className='flex'>
                    <span className='issuer-label-pill'>Dirección</span>
                    <input
                      name='address'
                      className='input issuer-input'
                      value={issuer.address || ''}
                      onChange={handleIssuer}
                    />
                  </div>
                </div>

                {/* Derecha */}
                <div className='space-y-3'>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                    <div className='flex'>
                      <span className='issuer-label-pill'>Comuna</span>
                      <input
                        name='district'
                        className='input issuer-input'
                        value={issuer.district || ''}
                        onChange={handleIssuer}
                      />
                    </div>
                    <div className='flex'>
                      <span className='issuer-label-pill'>Ciudad</span>
                      <input
                        name='city'
                        className='input issuer-input'
                        value={issuer.city || ''}
                        onChange={handleIssuer}
                      />
                    </div>
                  </div>

                  <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                    <div className='flex'>
                      <span className='issuer-label-pill'>Email</span>
                      <input
                        name='email'
                        className='input issuer-input'
                        value={issuer.email || ''}
                        onChange={handleIssuer}
                      />
                    </div>
                    <div className='flex'>
                      <span className='issuer-label-pill'>Teléfono</span>
                      <input
                        name='phone_number'
                        className='input issuer-input'
                        value={issuer.phone_number || ''}
                        onChange={handleIssuer}
                      />
                    </div>
                  </div>

                  <div className='flex'>
                    <span className='issuer-label-pill'>Act. Econo.</span>
                    <input
                      name='business_activity'
                      className='input issuer-input'
                      value={issuer.business_activity || ''}
                      onChange={handleIssuer}
                    />
                  </div>
                </div>
              </div>

              {/* Campos ocultos */}
              <input type='hidden' name='rut' value={issuer.rut || ''} onChange={handleIssuer} />
              <input
                type='hidden'
                name='activity_code'
                value={issuer.activity_code || ''}
                onChange={handleIssuer}
              />
              <input
                type='hidden'
                name='sii_branch_code'
                value={issuer.sii_branch_code || ''}
                onChange={handleIssuer}
              />
            </div>
          )}
        </section>

        {/* === DATOS RECEPTOR – diseño tipo SII === */}
        <section className='bg-white rounded-md shadow border border-gray-200'>
          <SectionHeader
            title='Datos Receptor'
            open={showReceiver}
            onToggle={() => setShowReceiver(v => !v)}
          />

          {showReceiver && (
            <div className='px-4 pb-4 pt-3 space-y-3'>
              {/* Fila 1: Rut (2 campos) + Razón social */}
              <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                {/* Rut */}
                <div className='flex items-center gap-2'>
                  <div className='flex items-center'>
                    <span className='issuer-label-pill h-[38px]'>Rut</span>
                    <input
                      className='input issuer-input'
                      value={receiverRutBody}
                      onChange={handleReceiverRutBodyChange}
                      onBlur={handleReceiverRutBlur}
                      maxLength={8}
                      disabled={isSearchingEntity}
                      required
                    />
                  </div>

                  <span className='text-sm'>-</span>
                  <input
                    className='inputVer w-12 text-center px-2'
                    value={receiverRutDv}
                    onChange={handleReceiverRutDvChange}
                    onBlur={handleReceiverRutBlur}
                    disabled={isSearchingEntity}
                  />
                </div>

                {/* Razón Social */}
                <div className='flex'>
                  <span className='issuer-label-pill'>Razón Social</span>
                  <input
                    name='business_name'
                    className='input issuer-input'
                    value={receiver.business_name || ''}
                    onChange={handleReceiver}
                  />
                </div>
              </div>

              {/* Fila 2 y 3: Comuna / Act. Econ.  vs  Dirección / Ciudad */}
              <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                {/* Izquierda */}
                <div className='space-y-3'>
                  <div className='flex'>
                    <span className='issuer-label-pill'>Comuna</span>
                    <input
                      name='district'
                      className='input issuer-input'
                      value={receiver.district || ''}
                      onChange={handleReceiver}
                    />
                  </div>

                  <div className='flex'>
                    <span className='issuer-label-pill'>Act. Econo.</span>
                    <input
                      name='business_activity'
                      className='input issuer-input'
                      value={receiver.business_activity || ''}
                      onChange={handleReceiver}
                    />
                  </div>

                  <div className='flex'>
                    <span className='issuer-label-pill'>Contacto</span>
                    <input
                      name='contact'
                      className='input issuer-input'
                      value={receiver.contact || ''}
                      onChange={handleReceiver}
                    />
                  </div>
                </div>

                {/* Derecha */}
                <div className='space-y-3'>
                  <div className='flex'>
                    <span className='issuer-label-pill'>Dirección</span>
                    <input
                      name='address'
                      className='input issuer-input'
                      value={receiver.address || ''}
                      onChange={handleReceiver}
                    />
                  </div>

                  <div className='flex'>
                    <span className='issuer-label-pill'>Ciudad</span>
                    <input
                      name='city'
                      className='input issuer-input'
                      value={receiver.city || ''}
                      onChange={handleReceiver}
                    />
                  </div>
                </div>
              </div>

              {/* Campo oculto real del rut para el payload */}
              <input
                type='hidden'
                name='rut'
                value={receiver.rut || ''}
                onChange={handleReceiver}
              />
            </div>
          )}
        </section>

        {/* PRODUCTOS */}
        <section className='p-4 bg-white rounded-md shadow space-y-4 border border-gray-200'>
          <div className='flex items-center justify-between'>
            <div>
              <div className='flex items-center gap-2'>
                <h2 className='font-semibold text-sm md:text-base'>Cod Producto</h2>
                <input
                  type='checkbox'
                  checked={useProductCode}
                  onChange={e => setUseProductCode(e.target.checked)}
                />
              </div>

              <p className='text-xs text-gray-500'>
                Nombre del producto, cantidad y precio son obligatorios.
              </p>
            </div>

            {/* botón de arriba ELIMINADO, ahora va abajo como en el SII */}
          </div>

          {/* Tabla de productos */}
          <div className='overflow-auto bg-white'>
            <table className='w-full text-xs md:text-sm detalle-table'>
              <thead className='h-10 bg-gray-100'>
                <tr>
                  {useProductCode && <th className='w-28'>Tipo código</th>}
                  {useProductCode && <th className='w-24'>Código</th>}

                  <th className='w-[220px] text-start'>
                    Nombre Producto <span className='text-red-500'>*</span>
                  </th>

                  {/* Columna para el checkbox de descripción (solo estilo, tu lógica queda igual) */}
                  <th className='w-16 text-center'>Descrip.</th>

                  <th className='w-20'>
                    Cantidad <span className='text-red-500'>*</span>
                  </th>

                  <th className='w-20'>Unidad</th>

                  <th className='w-28'>
                    Precio <span className='text-red-500'>*</span>
                  </th>

                  <th className='w-24'>% Desc.</th>

                  <th className='w-24 text-right'>SubTotal</th>

                  <th className='w-10'></th>
                </tr>
              </thead>

              <tbody>
                {details.map((d, idx) => {
                  const qty = Number(d.quantity) || 0
                  const price = Number(d.unit_price) || 0
                  const pct = Number(d.discount_percent) || 0

                  const lineGross = qty * price
                  const lineDiscount = (lineGross * pct) / 100
                  const lineNet = lineGross - lineDiscount

                  const formatMoney = value => value.toLocaleString('es-CL')

                  return (
                    <React.Fragment key={idx}>
                      {/* Fila principal */}
                      <tr>
                        {useProductCode && (
                          <td>
                            <input
                              className='input'
                              value={d.item_type_code}
                              onChange={e => updateDetail(idx, 'item_type_code', e.target.value)}
                              placeholder='Ej: CPPU'
                            />
                          </td>
                        )}

                        {useProductCode && (
                          <td>
                            <input
                              className='input'
                              value={d.item_code}
                              onChange={e => updateDetail(idx, 'item_code', e.target.value)}
                              placeholder='1500'
                            />
                          </td>
                        )}

                        {/* Nombre producto */}
                        <td>
                          <input
                            className='input'
                            value={d.item_name}
                            onChange={e => updateDetail(idx, 'item_name', e.target.value)}
                            required
                            placeholder='Nombre del producto'
                          />
                        </td>

                        {/* Checkbox descripción (tu estado/handler aquí si ya lo tienes) */}
                        <td className='text-center align-middle'>
                          <input
                            type='checkbox'
                            checked={d.showDescription || false}
                            onChange={e => updateDetail(idx, 'showDescription', e.target.checked)}
                          />
                        </td>

                        {/* Cantidad */}
                        <td>
                          <input
                            type='number'
                            className='input text-right'
                            value={d.quantity}
                            onChange={e => updateDetail(idx, 'quantity', e.target.value)}
                            required
                            min='1'
                          />
                        </td>

                        {/* Unidad (texto libre) */}
                        <td>
                          <input
                            type='text'
                            className='input text-center'
                            value={d.unit}
                            onChange={e => updateDetail(idx, 'unit', e.target.value)}
                            placeholder='UN'
                          />
                        </td>

                        {/* Precio */}
                        <td>
                          <input
                            type='number'
                            className='input text-right'
                            value={d.unit_price}
                            onChange={e => updateDetail(idx, 'unit_price', e.target.value)}
                            required
                            min='0'
                          />
                        </td>

                        {/* % Desc. */}
                        <td>
                          <input
                            type='number'
                            className='input text-right'
                            value={d.discount_percent}
                            onChange={e => updateDetail(idx, 'discount_percent', e.target.value)}
                            min='0'
                            max='100'
                          />
                        </td>

                        {/* Subtotal */}
                        <td className='text-right whitespace-nowrap border border-[#e5e7eb] rounded-[0.4rem] pr-2'>
                          ${formatMoney(Math.round(lineNet))}
                        </td>

                        {/* Basurero */}
                        <td className='text-center'>
                          <button
                            type='button'
                            className='trash-btn'
                            onClick={() => removeDetail(idx)}
                            aria-label='Eliminar ítem'
                          >
                            <DeleteOutlineOutlinedIcon fontSize='small' />
                          </button>
                        </td>
                      </tr>

                      {/* Fila de descripción extendida: se muestra cuando tu lógica lo indique */}
                      {d.showDescription && (
                        <tr className='descripcion-row'>
                          {useProductCode && <td></td>}
                          {useProductCode && <td></td>}
                          {/* colSpan para que la descripción se vea como en el SII */}
                          <td colSpan={useProductCode ? 7 : 6}>
                            <textarea
                              className='input descripcion-textarea'
                              value={d.item_description}
                              onChange={e => updateDetail(idx, 'item_description', e.target.value)}
                              placeholder='Ingrese sólo si requiere ingresar una descripción extendida del producto o servicio'
                            />
                          </td>
                          <td></td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>

            {/* Botón abajo, como en la imagen del SII */}
            <div className='flex justify-center mt-3 h-12 items-center bg-transparent'>
              <button type='button' className='cursor-pointer inline-flex items-center justify-center gap-2 rounded-full bg-accent2 px-6 py-2.5 text-xs font-bold text-white shadow-lg shadow-black/20 transition-all hover:bg-accent2/90 active:scale-95' onClick={addDetail}>
                Agrega línea de Detalle
              </button>
            </div>
          </div>
        </section>

        {/* Referencias */}
        <section className='bg-gray-100 p-4 rounded-md shadow border border-gray-200 space-y-4'>
          {/* Cabecera: checkbox + forma de pago */}
          <div className='grid grid-cols-5 grid-rows-1 gap-3'>
            <label className='col-span-2 flex items-center gap-2 text-sm'>
              <input
                type='checkbox'
                checked={showReferencesBox}
                onChange={e => {
                  const checked = e.target.checked
                  setShowReferencesBox(checked)

                  if (checked && references.length === 0) {
                    setReferences([
                      { ...emptyReference },
                      { ...emptyReference },
                      { ...emptyReference }
                    ])
                  }
                }}
              />
              <span>Referencias</span>
            </label>

            <div className='col-span-3 col-start-3'>
              <span className='text-sm text-gray-700'>Forma de Pago</span>
              <select
                name='payment_method'
                className='input w-40'
                value={header.payment_method}
                onChange={handleHeader}
              >
                <option value=''>Seleccione...</option>
                <option value='1'>Contado</option>
                <option value='2'>Crédito</option>
                <option value='3'>Gratuito</option>
              </select>
            </div>
          </div>

          {showReferencesBox && (
            <div className='overflow-auto'>
              {/* Tabla de referencias estilo SII */}
              <table className='w-full text-xs md:text-sm refs-table'>
                <thead>
                  <tr>
                    <th className='text-start min-w-[180px]'>Tipo de documento</th>
                    <th className='text-start w-28'>Folio Ref.</th>
                    <th className='text-start w-40'>Fecha Ref.</th>
                    <th className='text-start'>Razón Referencia</th>
                    <th className='text-start w-10'></th>
                  </tr>
                </thead>

                <tbody className='bg-white rounded-md'>
                  {references.map((r, idx) => (
                    <tr key={idx}>
                      {/* Tipo de documento */}
                      <td>
                        <select
                          className='input'
                          value={r.dte_type_code}
                          onChange={e => updateReference(idx, 'dte_type_code', e.target.value)}
                        >
                          <option value=''>Seleccionar tipo</option>
                          <option value='110'>Factura de Exportación Electrónica (110)</option>
                          <option value='111'>
                            Nota de Débito de Exportación Electrónica (111)
                          </option>
                          <option value='112'>
                            Nota de Crédito de Exportación Electrónica (112)
                          </option>
                          <option value='33'>Factura Electrónica (33)</option>
                          <option value='34'>Factura Exenta Electrónica (34)</option>
                          <option value='39'>Boleta Electrónica (39)</option>
                          <option value='41'>Boleta Exenta Electrónica (41)</option>
                          <option value='52'>Guía Despacho Electrónica (52)</option>
                          <option value='56'>Nota de Débito Electrónica (56)</option>
                          <option value='61'>Nota de Crédito Electrónica (61)</option>
                          <option value='801'>Orden de Compra (801)</option>
                          <option value='HES'>Hoja Entrada Servicio (HES)</option>
                        </select>
                      </td>

                      {/* Folio ref */}
                      <td>
                        <input
                          className='input'
                          placeholder=''
                          type='text'
                          value={r.reference_folio}
                          onChange={e => updateReference(idx, 'reference_folio', e.target.value)}
                        />
                      </td>

                      {/* Fecha ref (mantengo tu input date para no tocar la lógica) */}
                      <td>
                        <input
                          className='input'
                          type='date'
                          value={r.reference_date}
                          onChange={e => updateReference(idx, 'reference_date', e.target.value)}
                        />
                      </td>

                      {/* Razón referencia */}
                      <td>
                        <input
                          className='input'
                          placeholder=''
                          value={r.reference_reason}
                          onChange={e => updateReference(idx, 'reference_reason', e.target.value)}
                        />
                      </td>

                      {/* Basurero */}
                      <td className='text-center align-middle'>
                        <button
                          type='button'
                          className='trash-btn'
                          onClick={() => removeReference(idx)}
                          aria-label='Eliminar referencia'
                        >
                          <DeleteOutlineOutlinedIcon fontSize='small' />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Botón para agregar más referencias, centrado abajo */}
              <div className='flex justify-center mt-3 h-12 items-center bg-transparent'>
                <button type='button' className='cursor-pointer inline-flex items-center justify-center gap-2 rounded-full bg-accent2 px-6 py-2.5 text-xs font-bold text-white shadow-lg shadow-black/20 transition-all hover:bg-accent2/90 active:scale-95' onClick={addReference}>
                  Agregar referencia
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Totales */}
        <FacturaTotales details={details} dteCode={dteCode} />

        <section className='flex items-center justify-center gap-3'>
          <button className='cursor-pointer rounded-lg bg-primary px-6 py-2.5 text-sm font-bold text-white shadow-lg shadow-black/20 transition-all hover:bg-primary/90 active:scale-95' type='submit' disabled={isEmitting}>
            {onContinue ? 'Validar y vizualizar' : 'Emitir Factura'}
          </button>
          <button type='button' onClick={handleReset} className='cursor-pointer rounded-lg bg-primary px-6 py-2.5 text-sm font-bold text-white shadow-lg shadow-black/20 transition-all hover:bg-primary/90 active:scale-95'>
            Limpiar
          </button>
          <button type='button' onClick={onBack} className='cursor-pointer rounded-lg bg-primary px-6 py-2.5 text-sm font-bold text-white shadow-lg shadow-black/20 transition-all hover:bg-primary/90 active:scale-95'>
            Volver
          </button>
        </section>

        <style>{`
          .input {
            width: 100%;
            padding: 0.35rem 0.6rem;
            border: 1px solid #e5e7eb;
            border-radius: 0.4rem;
            outline: none;
            font-size: 0.8rem;
          }
          .inputVer {
            padding: 0.35rem 0.6rem;
            border: 1px solid #e5e7eb;
            border-radius: 0.4rem;
            outline: none;
            font-size: 0.8rem;
          }
        

          .input:focus, .inputVer:focus {
            box-shadow: 0 0 0 2px rgba(59,130,246,.3);
            border-color: #93c5fd;
          }

    
          .issuer-label-pill {
            display: inline-flex;
            align-items: center;
            padding: 0.3rem 0.6rem;
            font-size: 0.75rem;
            font-weight: 600;
            background: #f3f4f6;
            border: 1px solid #d1d5db;
            border-right: 0;
            border-radius: 0.4rem 0 0 0.4rem;
            white-space: nowrap;
          }

          .issuer-input {
            border-radius: 0 0.4rem 0.4rem 0;
          }

          .trash-btn {
            color: #ef4444; 
            cursor: pointer;
          }
          .trash-btn:hover {
            color: #b91c1c;     
          }

          .refs-table {
            border-collapse: separate;
            border-spacing: 0 4px;  
          }

          .refs-table thead th {
            background: #f3f4f6;
            border-bottom: 1px solid #d1d5db;
            padding: 0.4rem 0.5rem;
            font-weight: 600;
            text-align: left;
          }

          .refs-table tbody td {
            padding: 0.3rem 0.5rem;
            vertical-align: middle;
            background: #ffffff;  
            border-top: 1px solid #e5e7eb;
            border-bottom: 1px solid #e5e7eb;
          }

          .detalle-table {
            border-collapse: separate;
            border-spacing: 0 6px;  
          }
        `}</style>
      </form>
    </div>
  )
}

